import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { logger, reportException } from '@origin-digital/reporting-client';
import {
  IContractAccountInvoice,
  IContractAccountStatus,
} from '@origin-digital/payment-service-dapi-contract';
import { formatAmount } from '@origin-digital/formatters';

import {
  routes,
  divisionCodeMap,
  invalidRedirectParam,
  lpgDivisionCode,
  lpgDivisionId,
  IDivisionIdWithLpg,
} from 'src/consts';
import { EPaymentType, AccountBackend } from 'src/interfaces';
import { useAppContext } from 'src/context/AppContext';
import { useSharedAppContext } from 'src/context/SharedAppContext';
import { getAccountBalances, getInvoiceInfo, verifyContractAccount } from 'src/services';
import { Loading } from 'src/components/Loading';
import {
  isKrakenId,
  isMyAccount,
  hasCaid,
  hasValidAmount,
  hasAmount,
  hasLength,
  isAuthenticated,
  hasKrakenId,
} from 'src/utils/helpers';

type IRedirect = {
  name: string;
  path: string;
  invalidRedirect?: string;
};

export const BaseScreen: React.SFC = () => {
  const { setContext } = useAppContext();
  const sharedAppContext = useSharedAppContext();
  const { invoiceId } = useParams<{ invoiceId?: string }>();
  const [redirectTo, setRedirectTo] = useState<IRedirect>();
  const [verifing, setVerifying] = useState<boolean>(false);

  const hasInvoice = (): boolean => !!invoiceId;
  const hasLpgParams = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    return params.get('product') === lpgDivisionCode.toLowerCase();
  };

  const shouldRedirectToMyAccount = (): boolean =>
    isAuthenticated() && !isMyAccount() && !hasLpgParams();

  const getAmountDue = ({
    paymentType,
    instalment,
    amountDue,
  }: IContractAccountInvoice): number => {
    const isInstalment = () => {
      return (paymentType as EPaymentType) === EPaymentType.PaymentScheme && !!instalment;
    };

    if (isInstalment()) {
      return instalment.amountDue;
    }

    return amountDue;
  };

  const processAuthV1 = async () => {
    try {
      const { properties } = sharedAppContext;

      if (!properties || !properties.length) {
        logger.info('properties not available');

        return;
      }
      let { contractAccountId } = sharedAppContext;
      let property = properties.find(({ premiseId }) => premiseId === sharedAppContext.premiseId);

      if (hasCaid()) {
        contractAccountId = new URLSearchParams(window.location.search).get('caid');

        if (contractAccountId !== sharedAppContext.contractAccountId) {
          sharedAppContext.actions.setContractAccountId(contractAccountId);
        }
      }

      if (hasInvoice()) {
        const invoice = await getInvoiceInfo({ invoice: invoiceId });
        property = properties.find(({ contractAccounts }) =>
          contractAccounts.some(
            (contractAccount) => contractAccount.contractAccountId === invoice.contractAccountId,
          ),
        );
        contractAccountId = invoice.contractAccountId;

        if (property.premiseId !== sharedAppContext.premiseId) {
          sharedAppContext.actions.setPremiseId(property.premiseId);
        }

        if (contractAccountId !== sharedAppContext.contractAccountId) {
          sharedAppContext.actions.setContractAccountId(contractAccountId);
        }
      }

      if (!hasCaid() && !hasInvoice() && property.contractAccounts.length > 1) {
        return setRedirectTo({
          name: 'select-contract-account',
          path: routes.selectContractAccount,
        });
      }
      const divisionCode = property.contractAccounts.find(
        (contractAccount) => contractAccount.contractAccountId === contractAccountId,
      ).divisionCode;

      // has caid or has invoice or has service length === 1
      const accountBalances = await getAccountBalances({
        contractAccountIds: contractAccountId,
      });
      const amount = accountBalances.contractAccounts.reduce((balance, accountBalance) => {
        if (accountBalance.contractAccountId === contractAccountId) {
          return getAmountDue(accountBalance);
        }

        return balance;
      }, 0);

      const showAmountDue = hasValidAmount(amount.toString());
      const amountDue = (amount / 100).toString();

      setContext({
        address: property.premiseName,
        divisionId: divisionCodeMap[divisionCode],
        contractAccountId,
        showAmountDue,
        payAmountDue: showAmountDue,
        amount: showAmountDue ? amountDue : '',
        amountDue: hasAmount(amount.toString()) ? amountDue : undefined,
        amountProvided: '',
      });

      return setRedirectTo({
        name: 'select-payment-method',
        path: routes.selectPaymentMethod,
        invalidRedirect: `?caid=${contractAccountId}`,
      });
    } catch (error) {
      logger.warn('getAccountBalances()', error);

      throw Error(error);
    }
  };

  const processAuthV2 = async () => {
    try {
      const { addresses } = sharedAppContext;
      let { contractAccountId } = sharedAppContext;
      let property = addresses.find(({ id }) => id === sharedAppContext.selectedAddressId);

      if (hasCaid()) {
        contractAccountId = new URLSearchParams(window.location.search).get('caid');
        sharedAppContext.actions.setSelectedServiceId(contractAccountId);
      }

      if (hasInvoice()) {
        const invoice = await getInvoiceInfo({ invoice: invoiceId });
        property = addresses.find(({ services }) =>
          services.some((service) => service.backendId === invoice.contractAccountId),
        );
        contractAccountId = invoice.contractAccountId;
        sharedAppContext.actions.setSelectedAddressId(property.id);
        sharedAppContext.actions.setContractAccountId(contractAccountId);
      }

      if (!hasCaid() && !hasInvoice()) {
        if (property.services.length > 1) {
          return setRedirectTo({
            name: 'select-contract-account',
            path: routes.selectContractAccount,
          });
        }
      }
      const divisionCode = property.services.find(
        (service) => service.backendId === contractAccountId,
      ).type;

      // has caid or has invoice or has service length === 1
      const accountBalances = await getAccountBalances({ contractAccountIds: contractAccountId });
      const amount = accountBalances.contractAccounts.reduce((balance, accountBalance) => {
        if (accountBalance.contractAccountId === contractAccountId) {
          return getAmountDue(accountBalance);
        }

        return balance;
      }, 0);

      const showAmountDue = hasValidAmount(amount.toString());
      const amountDue = (amount / 100).toString();

      setContext({
        address: property.name,
        divisionId: divisionCodeMap[divisionCode],
        contractAccountId,
        showAmountDue,
        payAmountDue: showAmountDue,
        amount: showAmountDue ? amountDue : '',
        amountDue: hasAmount(amount.toString()) ? amountDue : undefined,
        amountProvided: '',
      });

      return setRedirectTo({
        name: 'select-payment-method',
        path: routes.selectPaymentMethod,
        invalidRedirect: `?caid=${contractAccountId}`,
      });
    } catch (error) {
      logger.warn('getAccountBalances()', error);

      throw Error(error);
    }
  };

  const processInvoice = async () => {
    try {
      const { amount, contractAccountId } = await getInvoiceInfo({ invoice: invoiceId });
      const { fuelType: divisionId } = await verifyContractAccount({ contractAccountId });

      const showAmountDue = hasValidAmount(amount);
      const amountDue = (parseFloat(amount) / 100).toString();

      setContext({
        divisionId: divisionId as IDivisionIdWithLpg,
        contractAccountId,
        showAmountDue,
        payAmountDue: showAmountDue,
        amount: showAmountDue ? amountDue : '',
        amountDue: hasAmount(amount) ? amountDue : undefined,
        amountProvided: '',
      });

      return setRedirectTo({
        name: 'select-payment-method',
        path: routes.selectPaymentMethod,
        invalidRedirect: invoiceId,
      });
    } catch (error) {
      reportException('getInvoice()', error);

      return setRedirectTo({
        name: 'select-contract-account',
        path: routes.selectContractAccount,
      });
    }
  };

  const processLpg = () => {
    const params = new URLSearchParams(window.location.search);

    const orderNumber = params.get('orderNumber');
    const customerNumber = params.get('customerNumber');
    const amount = params.get('amount');

    if (!customerNumber || !amount) {
      throw Error('LPG parameters incomplete');
    }

    if (parseFloat(amount) < 1) {
      throw Error(`LPG amount invalid (< 1): ${amount}`);
    }

    if (orderNumber && !hasLength(7)(orderNumber)) {
      throw Error(`LPG order number invalid: ${orderNumber}`);
    }

    if (!hasLength(8, 10)(customerNumber) || isNaN(parseFloat(customerNumber))) {
      throw Error(`LPG customer number invalid: ${customerNumber}`);
    }

    if (!hasAmount(amount)) {
      throw Error(`LPG amount invalid (NaN): ${amount}`);
    }

    setContext({
      divisionId: lpgDivisionId,
      contractAccountId: customerNumber,
      orderNumber,
      amountDue: amount,
      amount,
      showAmountDue: true,
    });

    return setRedirectTo({
      name: 'select-payment-method',
      path: routes.selectPaymentMethod,
      invalidRedirect: `${window.location.search}`,
    });
  };

  const processKraken = () => {
    const params = new URLSearchParams(window.location.search);

    const accountNumber = params.get('accountNumber');
    const amount = hasValidAmount(params.get('amount'))
      ? formatAmount(+params.get('amount') / 100)
      : '';

    setContext({
      contractAccountId: accountNumber,
      amountProvided: amount,
      backend: AccountBackend.kraken,
    });

    setRedirectTo({
      name: 'select-payment-method',
      path: routes.selectPaymentMethod,
      invalidRedirect: `?caid=${accountNumber}${!!amount ? `&amount=${params.get('amount')}` : ''}`,
    });
  };

  const processUnauthCaid = async () => {
    const params = new URLSearchParams(window.location.search);
    const contractAccountId = params.get('caid');

    const redirectToSelectContractAccount = () => {
      setRedirectTo({
        name: 'select-contract-account',
        path: routes.selectContractAccount,
      });
    };

    const redirectToSelectPaymentMethod = () => {
      setRedirectTo({
        name: 'select-payment-method',
        path: routes.selectPaymentMethod,
        invalidRedirect: `?caid=${contractAccountId}`,
      });
    };

    if (verifing) {
      return;
    }

    setVerifying(true);

    if (isKrakenId(contractAccountId)) {
      setContext({
        contractAccountId,
        backend: AccountBackend.kraken,
      });

      return redirectToSelectPaymentMethod();
    }

    try {
      const { fuelType: divisionId, verified, status } = await verifyContractAccount({
        contractAccountId,
      });

      if (!verified || status === IContractAccountStatus.SETUP) {
        return redirectToSelectContractAccount();
      }

      setContext({
        divisionId: divisionId as IDivisionIdWithLpg,
        contractAccountId,
        backend: AccountBackend.sap,
      });

      redirectToSelectPaymentMethod();
    } catch (e) {
      return redirectToSelectContractAccount();
    }
  };

  useEffect(
    () => {
      if (shouldRedirectToMyAccount()) {
        logger.info('redirect to my account');
        window.location.assign(`/my-account/pay/${invoiceId || ''}${window.location.search}`);
        return;
      }

      if (hasLpgParams()) {
        processLpg();

        return;
      }

      if (isMyAccount()) {
        const { addresses } = sharedAppContext;
        if (addresses && addresses.length > 0) {
          processAuthV2();
        } else {
          processAuthV1();
        }

        return;
      }

      if (hasInvoice()) {
        processInvoice();

        return;
      }

      if (hasCaid()) {
        processUnauthCaid();
        return;
      }

      if (hasKrakenId()) {
        processKraken();
        return;
      }

      setRedirectTo({
        name: 'select-contract-account',
        path: routes.selectContractAccount,
      });
    },
    [sharedAppContext.properties],
  );

  if (redirectTo) {
    const { name, path, invalidRedirect } = redirectTo;

    return (
      <div data-id={`redirect-${name}`}>
        <Redirect
          to={`${path}${
            invalidRedirect ? `?${invalidRedirectParam}=${encodeURIComponent(invalidRedirect)}` : ''
          }`}
        />
      </div>
    );
  }

  return <Loading />;
};
